import { RulesLogic } from 'json-logic-js';
import { kebabCase } from 'lodash';
import { ComponentProps, FC, ReactNode, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Navigate, NavLink, Route } from 'react-router-dom-v5-compat';

import { useComputedProperty } from 'dashboard/components/GridLayoutRender/computed-properties/useComputedProperty';
import { ModuleLayout } from 'dashboard/components/GridLayoutRender/ModuleLayout';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import { ComputedProperty } from 'dashboard/lib/case-layouts/computed-properties';
import { Module } from 'dashboard/lib/module';
import { Configuration } from 'dashboard/models/grid-layout';
import Badge from 'design-system/components/Badge';
import Spacer from 'design-system/components/Spacer';
import TabBar, { Tab } from 'design-system/components/TabBar';

export interface GridLayoutRenderProps<M extends Module, C extends Configuration<M>> {
  configuration: C;
  renderModule: (module: M) => ReactNode;
  runCodeWithContext: (code: string) => Promise<unknown>;
  runJsonLogicWithContext: (rules: RulesLogic) => Promise<boolean>;
}

interface LayoutTabs {
  tabs: ReactNode[];
  tabContents: ReactNode[];
}

export function GridLayoutRender<M extends Module, C extends Configuration<M>>({
  configuration,
  renderModule,
  runCodeWithContext,
  runJsonLogicWithContext,
}: GridLayoutRenderProps<M, C>) {
  const { path, url } = useRouteMatch();

  const tabs: LayoutTabs = useMemo(() => {
    const layoutTabs: LayoutTabs = { tabs: [], tabContents: [] };
    configuration.tabs.forEach((tab) => {
      const urlName = kebabCase(tab.name);

      // hack to workaround tab badges only existing for case layouts
      const badgeContent = 'badge' in tab ? (tab['badge'] as ComputedProperty) : undefined;

      layoutTabs.tabs.push(
        <LayoutTab
          key={tab.name}
          id={tab.token}
          path={urlName}
          badgeContent={badgeContent}
          runCodeWithContext={runCodeWithContext}
        >
          {tab.name}
        </LayoutTab>,
      );

      const tabContent = (
        <Route
          key={tab.token}
          path={urlName}
          element={
            <ModuleLayout
              key={tab.token} // Required to ensure that a new component renders when the tab changes
              modules={tab.modules}
              renderModule={renderModule}
              runCodeWithContext={runCodeWithContext}
              runJsonLogicWithContext={runJsonLogicWithContext}
            />
          }
        />
      );

      layoutTabs.tabContents.push(tabContent);
    });

    // redirect index route to the first tab
    const firstTab = configuration.tabs[0];
    if (firstTab) {
      const urlName = kebabCase(firstTab.name);
      layoutTabs.tabContents.push(
        <Route path='*' index key='redirect' element={<Navigate to={urlName} replace />} />,
      );
    }

    return layoutTabs;
  }, [configuration, path, url, renderModule, runCodeWithContext, runJsonLogicWithContext]);

  return (
    <>
      <TabBar data-test='grid-layout-render__tabs'>{tabs.tabs}</TabBar>
      <Spacer height={3} />
      <SentryRoutes>{tabs.tabContents}</SentryRoutes>
    </>
  );
}

interface LayoutTabProps extends ComponentProps<typeof Tab> {
  path: string;
  badgeContent?: ComputedProperty;
  runCodeWithContext: (code: string) => Promise<unknown>;
}

export const LayoutTab: FC<LayoutTabProps> = ({
  id,
  children,
  badgeContent,
  path,
  runCodeWithContext,
}) => {
  const [badge] = useComputedProperty<string>(runCodeWithContext, badgeContent);

  return (
    <Tab
      as={NavLink}
      to={path}
      id={`case-tab_${id}`}
      badge={badge ? <Badge>{badge}</Badge> : undefined}
    >
      {children}
    </Tab>
  );
};
